'use client';

import MapLayerControl, { MapLayerController } from '@/components/map/MapLayerControl';
import UsercentricsScriptEnabler from '@/components/usercentricsScriptEnabler/UsercentricsScriptEnabler';
import { useSettings } from '@/providers/SettingsProvider';
import type { ComponentMapsStoryblok } from '@/types/component-types-sb';
import { Loader } from '@googlemaps/js-api-loader';
import { storyblokEditable } from '@storyblok/react/rsc';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './Map.module.scss';

function useGoogleMapsLoader(): Loader | null {
  const settings = useSettings();
  const [loader, setLoader] = useState<Loader | null>(null);

  useEffect(() => {
    if (!loader && settings) {
      const apiKey = settings?.google_maps_api;
      if (!apiKey) {
        console.warn('Google Maps API Key is not configured!');
        return;
      }

      setLoader(
        new Loader({
          apiKey: settings!.google_maps_api!,
          language: 'de',
        })
      );
    }
  }, [loader, settings]);

  return loader;
}

function handleLocationError(
  browserHasGeolocation: boolean,
  infoWindow: google.maps.InfoWindow,
  pos: google.maps.LatLngLiteral,
  map: unknown
) {
  infoWindow.setPosition(pos);
  infoWindow.setContent(
    browserHasGeolocation
      ? 'Fehler beim Abfragen der aktuellen Position.'
      : 'Dieser Browser unterstützt keine Positionsabfrage.'
  );
  infoWindow.open(map);
}

const MapComponent: React.FC<{ blok: ComponentMapsStoryblok }> = ({ blok }) => {
  const loader = useGoogleMapsLoader();
  const mapRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [layerControllers, setLayerControllers] = useState<MapLayerController[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    const initMap = async () => {
      const mapRefElement = mapRef.current;
      if (!loader || !mapRefElement) {
        return;
      }

      try {
        //config the center of the map, use düsseldorf as a fallback
        const zoom: number = parseInt(blok.zoom_level ?? '10');
        const center: google.maps.LatLngLiteral = {
          lat: parseFloat(blok.latitude ?? '51.233334'),
          lng: parseFloat(blok.longitude ?? '6.783333'),
        };

        //this will load maps as a library to show the actual map
        const { Map, KmlLayer } = await loader.importLibrary('maps');
        const map = new Map(mapRefElement, { center, zoom });
        const infoWindow = new google.maps.InfoWindow();

        const locationButton = document.createElement('button');
        locationButton.textContent = 'Aktuelle Position';
        locationButton.setAttribute('data-swd-button', '');
        locationButton.setAttribute('data-variant', 'ctaFull');
        locationButton.setAttribute('data-size', 'medium');
        locationButton.classList.add(classes.MapPositionButton);
        map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(locationButton);

        locationButton.addEventListener('click', () => {
          // Try HTML5 geolocation.
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
              (position: GeolocationPosition) => {
                const pos = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                };

                infoWindow.setPosition(pos);
                infoWindow.setContent('Aktuelle Position');
                infoWindow.open(map);
                map.setCenter(pos);
              },
              () => {
                handleLocationError(true, infoWindow, map.getCenter()!, map);
              }
            );
          } else {
            // Browser doesn't support Geolocation
            handleLocationError(false, infoWindow, map.getCenter()!, map);
          }
        });

        //if a kml layer is defined, it should be loaded as well
        const controllers: MapLayerController[] = [];
        for (const layer of blok.layers ?? []) {
          const url = `https://www.google.com/maps/d/u/0/kml?mid=${layer.my_maps_id}`;
          const kmlLayer = new KmlLayer({ map, url });
          if (layer.can_toggle) {
            controllers.push(new MapLayerController(layer._uid, layer.toggle_label, map, kmlLayer));
          }
        }
        setLayerControllers(controllers);
      } catch (error) {
        setError(t('Die Karte konnte nicht geladen werden. Bitte versuchen sie es später wieder.')); // Set error state
        console.error('Error loading map:', error);
      }
    };

    initMap();
  }, [loader, blok.latitude, blok.longitude, blok.zoom_level, blok.my_maps_id, blok.layers, t]);

  if (error) {
    return (
      <div {...storyblokEditable(blok)}>
        <div className={classes.ErrorMessage}>{error}</div>
      </div>
    );
  }

  return (
    <div id={blok.anchor || undefined} className={classes.MapComponent} {...storyblokEditable(blok)}>
      <div {...storyblokEditable(blok)}>
        <div className={classes.MapHeader}>
          {blok.title && <h2>{blok.title}</h2>}
          {blok.subtitle && <h3>{blok.subtitle}</h3>}
        </div>
        {!!layerControllers.length && <MapLayerControl items={layerControllers} />}
      </div>
      <div id={`map_${blok._uid}`} data-testid="map" ref={mapRef} className={classes.Map}></div>
    </div>
  );
};

const Map: React.FC<{ blok: ComponentMapsStoryblok }> = ({ blok }) => {
  return (
    <UsercentricsScriptEnabler
      id={blok.anchor || undefined}
      image={blok.consent_image}
      type="google_maps"
      isFullScreen={false}
      noWrap
    >
      <MapComponent blok={blok} />
    </UsercentricsScriptEnabler>
  );
};

export default Map;
